/**
 * Human-readable Excel column headers for pilot data export.
 */

const EXACT_LABELS = {
  participantEmail: 'Participant email address',
  participantDisplayName: 'Participant display name',
  userRole: 'User Role',
  authCreatedAt: 'Auth account created at',
  authLastSignInAt: 'Auth last sign-in at',
  profileSource: 'Profile data source',
  exportAuthSyncedAt: 'Profile synced at',
  enrolledModules: 'Enrolled modules',
  completedModules: 'Completed modules',
  updatedAt: 'Profile last updated',
  moduleId: 'Module ID',
  createdAt: 'Created at',
  type: 'Feedback type',
  message: 'Feedback message',
  email: 'Email (on submission)',
  pageUrl: 'Page URL',
  enrollmentResearchConsentAccepted: 'Enrollment research consent accepted',
  consentSupplementQuestionnaireSkipped: 'Consent supplement questionnaire skipped',
  audioTapedPermission: 'Audio recording permission',
  videoTapedPermission: 'Video recording permission',
  wishRemainAnonymous: 'Wish to remain anonymous',
  wishAnonymousWithPseudonym: 'Wish to remain anonymous with pseudonym',
  pseudonym: 'Pseudonym',
  quoteWithNamePermission: 'Permission to quote with name',
  receiveStudySummary: 'Receive study summary',
  summaryContactInfo: 'Study summary contact information',
  followUpInterviewInterest: 'Follow-up interview interest',
  participantPrintedName: 'Participant printed name',
  participantDate: 'Participant signature date',
  additionalFeedback: 'Additional feedback (workplace survey)',
  q1IntroClarity: 'Introduction clarity response',
  q1Explain: 'Introduction clarity - explanation',
  q9MindfulEmployers: 'Mindful employers - open response',
  q10YearsStemmProfession: 'Years in STEMM profession',
  q11StemmProfessions: 'STEMM professions selected',
  text: 'Response text',
  cloudWord: 'Word-cloud word',
  storyKey: 'Story key',
  responseType: 'Response type (pause/act)',
  barrierKeys: 'Barrier keys selected',
  momentTypes: 'Meta-reflexivity moment types',
  otherText: 'Other (open text)',
  _message: 'Note',
};

const SEGMENT_LABELS = {
  optional: 'Optional demographics',
  awarenessBefore: 'Awareness before training',
  awarenessAfter: 'Awareness after training',
  experience: 'Training experience',
  scaleRatings: 'Participant review rating',
  scaleExplains: 'Participant review rating - explanation',
  ratingExplains: 'Participant review rating - explanation',
  ratings: 'Participant review rating',
};

const AWARENESS_STATEMENT_LABELS = {
  selfReflectiveEdi: 'I am self-reflective on the issues of equity, diversity and inclusion',
  recognizingBiases: 'I understand the importance of recognizing biases',
  mentalModelsAwareness:
    'I have an awareness of the mental models I use on a daily basis (internal thinking tools that help make sense of complex information)',
};

const EXPERIENCE_STATEMENT_LABELS = {
  highQuality: 'The training was high quality',
  metExpectations: 'The training met my expectations',
  relevantToRole: 'The training was relevant to my job role',
  instructorEngaging: 'The instructor was knowledgeable and engaging',
  balancePresentationActivities: 'There was a good balance of presentations and interactive activities',
  comfortableParticipating: 'I felt comfortable participating in discussions and asking questions',
  changedHowISeeMyself: 'As a result of this training, I have changed the way I look at myself',
  challengedHeldIdeas: 'This training has challenged some of my firmly held ideas',
  discoveredInconsistencies:
    'During this training I discovered inconsistencies in what I had previously believed to be right',
  awareDecisionsCreateBarriers:
    'This training helped me become more aware of how decisions made in the workplace can unintentionally create barriers to advancement',
  awareSystemsCreateBarriers:
    'This training helped me become more aware of how workplace systems can unintentionally create barriers to advancement',
};

const PARTICIPANT_REVIEW_SCALE_LABELS = {
  reflectsLivedPatterns:
    'The workplace module reflects patterns I recognize from lived experience, even if the details are not exactly my own',
  conceptsPrecise: 'The concepts used in the module are precise and meaningful rather than vague or oversimplified',
  decisionPointsSystems:
    'The module makes specific decision points (e.g., hiring, promotion, visibility, evaluation) easier to notice and helpful to understand that systems - not individuals - are the focus',
  interactiveEngaging:
    'The module is interactive enough to keep sustained interest, with good use of videos and reflection questions',
  memorableContent: 'There are images, phrases, or ideas that I would likely remember over time',
  instructionsClear: 'The instructions are easy to hear/read and follow',
  barriersSouthAsianStem:
    'The module would help someone in a decision-making role understand the barriers that South Asian women in STEM face differently',
};

const OPTIONAL_FIELD_LABELS = {
  identitySelections: 'EDI identity selections (coded indices)',
  identityOther: 'EDI identity - another (specify)',
  followupInterview: 'Follow-up interview interest',
  currentRole: 'Current role',
  currentRoleOther: 'Current role (other)',
  yearsExperience: 'Years of experience',
  workContext: 'Work context',
  workContextOther: 'Work context (other)',
};

const STATEMENT_BY_SEGMENT = {
  awarenessBefore: AWARENESS_STATEMENT_LABELS,
  awarenessAfter: AWARENESS_STATEMENT_LABELS,
  experience: EXPERIENCE_STATEMENT_LABELS,
  scaleRatings: PARTICIPANT_REVIEW_SCALE_LABELS,
  scaleExplains: PARTICIPANT_REVIEW_SCALE_LABELS,
  ratingExplains: PARTICIPANT_REVIEW_SCALE_LABELS,
  ratings: PARTICIPANT_REVIEW_SCALE_LABELS,
};

/** @param {string} segment */
function humanizeSegment(segment) {
  if (OPTIONAL_FIELD_LABELS[segment]) return OPTIONAL_FIELD_LABELS[segment];
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  return segment
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\bEdi\b/g, 'EDI')
    .replace(/\bStemm\b/g, 'STEMM')
    .replace(/\bUid\b/g, 'UID')
    .replace(/\bId\b/g, 'ID')
    .replace(/\bUrl\b/g, 'URL')
    .replace(/^./, (c) => c.toUpperCase());
}

/**
 * @param {string} key - flattened export key (may use dots)
 * @returns {string}
 */
export function exportColumnKeyToLabel(key) {
  const raw = String(key || '').trim();
  if (!raw) return '';
  if (EXACT_LABELS[raw]) return EXACT_LABELS[raw];

  const parts = raw.split('.');
  if (parts.length === 1) return humanizeSegment(parts[0]);

  const [head, ...tail] = parts;
  const headLabel = SEGMENT_LABELS[head] || humanizeSegment(head);
  const last = tail[tail.length - 1];
  const statementMap = STATEMENT_BY_SEGMENT[head];
  if (statementMap && tail.length === 1 && statementMap[last]) {
    return `${headLabel} - ${statementMap[last]}`;
  }
  if (head === 'optional' && tail.length === 1 && OPTIONAL_FIELD_LABELS[last]) {
    return `${headLabel} - ${OPTIONAL_FIELD_LABELS[last]}`;
  }

  const tailLabels = tail.map((p) => {
    for (const map of Object.values(STATEMENT_BY_SEGMENT)) {
      if (map[p]) return map[p];
    }
    return humanizeSegment(p);
  });
  return `${headLabel} - ${tailLabels.join(' - ')}`;
}

/**
 * @param {Record<string, string | number | boolean>} row
 * @returns {Record<string, string | number | boolean>}
 */
export function relabelExportRowKeys(row) {
  const out = {};
  for (const [key, value] of Object.entries(row)) {
    out[exportColumnKeyToLabel(key)] = value;
  }
  return out;
}
